import React, { useCallback, useState } from 'react';

import ImageWithContent from 'components/feed/ImageWithContent';
import styled from 'styled-components';

import { SENTIMENTS } from 'constants/ProjectConstants';

import CommentDisplay from './CommentDisplay';
import CommentMedia from './CommentMedia';
import DotsMoreComponent, { getCommentsMoreMenuItems } from './CommentMore';
import EditCommentContainer from './EditCommentContainer';

export default function CommentContainer({
  id,
  fromEmployee,
  media,
  text,
  createdDate,
  macrosRes,
  addMention,
  contentState,
  feedId,
  updateFeedComment,
  actions,
  feedType,
  displayReply = true,
  employeeId,
  styleType = 'default',
  visibilityType,
  subType, // It was added for automated comment
  placeholder,
  renderEditCommentView,
  renderDisplayCommentView,
  talkingPointQuestionId = null,
  className,
  allowVisibilityEdit = true,
  useTeamIcon = false,
  visibilityChangeDenyTooltip = '',
  rightAlignCommentTime = false,
  isLastComment = false,
  isDisabled = false,
  feedback = false,
  handleCommentEdit = null,
  onlyEditingAllowed = false,
  enableKudos = true,
}) {
  const [isEditing, setEditing] = useState(false);
  const toggleEditing = useCallback(() => {
    setEditing(!isEditing);
  }, [isEditing]);

  const imageProps = { src: fromEmployee.getProfileImage() };
  const editViewProps = {
    id,
    feedId,
    contentState,
    media,
    toggleEditing,
    updateFeedComment,
    feedType,
    visibility: visibilityType.name,
    placeholder,
    talkingPointQuestionId,
    allowVisibilityEdit,
    useTeamIcon,
    visibilityChangeDenyTooltip,
    sentiment: macrosRes?.attributes?.sentiment,
    isKudos: feedback && macrosRes?.attributes?.sentiment === SENTIMENTS.appreciative,
    editComment: typeof handleCommentEdit === 'function' ? handleCommentEdit : undefined,
    enableKudos,
  };

  const commentMoreMenuItems = getCommentsMoreMenuItems({
    toggleEditing,
    isEditable: actions.edit.canPerform,
    cantEditReason: actions.edit.reason,
    visibilityType,
    onlyEditingAllowed,
  });

  const commentDisplayProps = {
    fromEmployee,
    text,
    macrosRes,
    createdDate,
    addMention,
    displayReply,
    styleType,
    placeholder,
    subType,
    commentMoreMenuItems,
    CommentMore: <DotsMoreComponent menuItems={commentMoreMenuItems} isDisabled={isDisabled} />,
    rightAlignCommentTime,
    isKudos: feedback && macrosRes?.attributes?.sentiment === SENTIMENTS.appreciative,
  };

  let content;
  if (isEditing) {
    content =
      typeof renderEditCommentView === 'function' ? (
        renderEditCommentView({ ...editViewProps })
      ) : (
        <ImageWithContent
          imageProps={imageProps}
          bottomGutter={isLastComment ? 0 : 1}
          align="flex-start"
          isLoggedInEmployeeComment={employeeId === fromEmployee.id}
          styleType={styleType}
          content={<StyledEditCommentContainer {...editViewProps} />}
        />
      );
  } else {
    content =
      typeof renderDisplayCommentView === 'function' ? (
        renderDisplayCommentView({ ...commentDisplayProps })
      ) : (
        <ImageWithContent
          imageProps={imageProps}
          bottomGutter={isLastComment ? 0 : 1}
          align="flex-start"
          isLoggedInEmployeeComment={employeeId === fromEmployee.id}
          styleType={styleType}
          content={<CommentDisplay {...commentDisplayProps} />}
        />
      );
  }

  return (
    <div className={className}>
      {content}
      {!isEditing && <CommentMedia media={media} />}
    </div>
  );
}

const StyledEditCommentContainer = styled(EditCommentContainer)`
  margin-bottom: 1rem;
`;
